import { useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';
import { adminRouteAccess } from '@rsos/capstone/src/constants/adminRouteAccess';
import NotFound from '@rsos/components/capstone/ErrorBoundary/NotFound';
import ErrorPage from '@rsos/components/capstone/ErrorPage';
import AppLoading from '@rsos/components/capstone/Layout/AppLoading';
import { useFirstAccessiblePath, useHasAccess } from '@rsos/hooks/useHasAccess';
import { psapSelector } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';

type RequireAccessProps = {
  /**
   * The route's key in adminRouteAccess (`@rsos/capstone/src/constants/adminRouteAccess`) —
   * the same full path the sidebar item links to.
   */
  path: string;
  /** Guarded content. Defaults to the router <Outlet />. */
  children?: React.ReactNode;
};

/**
 * Route guard for a single /admin route. Resolves the route's access descriptor
 * against the current user and, when they cannot be here, moves them somewhere
 * they can.
 *
 * Denial sends the user to the first admin route they *do* have access to, rather
 * than out of the app: reaching a denied route means a typed URL or a bookmark
 * that outlived a role change, and landing them on a page they can use keeps that
 * intent. It is also the same answer bare /admin gives, so the two agree. Only
 * when there is nowhere in /admin to put them is this NotFound.
 *
 * The redirect target is by construction a path that satisfies the same
 * `hasAccess` predicate this guard just failed, so it cannot hand back the path
 * that was denied and cannot ping-pong.
 *
 * Denial also has to be told apart from "not answerable yet", because moving the
 * user is not something they can undo. A descriptor that names a capability can
 * only be resolved once the org's PSAP has loaded, and until then the active
 * capability set is empty — indistinguishable from a capability the org does not
 * have. So while that fetch is in flight the guard waits, and if it failed it
 * renders the error page: relocating on a failed fetch turns a backend blip into
 * "admin silently sent me to the wrong page".
 */
const RequireAccess = ({ path, children = <Outlet /> }: RequireAccessProps) => {
  const allowed = useHasAccess(path);
  const elsewhere = useFirstAccessiblePath(Object.keys(adminRouteAccess));
  const { loading: psapLoading, hasErrors: psapHasErrors } =
    useSelector(psapSelector);

  // Only the capability check needs the PSAP. A route without one is answerable
  // from the user alone, so waiting on the PSAP there would be a loader for
  // nothing.
  const needsPsap = Boolean(adminRouteAccess[path]?.capabilityAccess);

  if (allowed) return <>{children}</>;
  if (needsPsap && Boolean(psapHasErrors)) return <ErrorPage />;
  if (needsPsap && psapLoading) return <AppLoading />;

  return elsewhere ? <Navigate to={elsewhere} replace /> : <NotFound />;
};

export default RequireAccess;
